import clsx from "clsx";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { getParentNodeByTag } from "utils/get-parent-node-by-class";

// Shared type for the hover state
type NavHoverState = {
  width: number;
  height: number;
  left: number;
  top: number;
  opacity: number;
  transition: string;
  borderRadius: string | number;
};

export const HoverEffect: FC<{ className?: string }> = ({ className = "" }) => {
  const [initialNavPosition] = useState<NavHoverState>({
    width: 0,
    height: 0,
    left: 0,
    top: 0,
    opacity: 0,
    transition: "0.1s opacity",
    borderRadius: 0,
  });

  const navHoverEffect = useRef<HTMLDivElement>(null);

  // Set values on the floating hover underline element
  const setNavHover = useCallback(
    ({
      width,
      height,
      left,
      top,
      opacity,
      transition,
      borderRadius,
    }: NavHoverState) => {
      const element = navHoverEffect.current;
      if (!element) return;

      element.style.width = `${width}px`;
      element.style.height = `${height}px`;
      element.style.left = `${left}px`;
      element.style.top = `${top}px`;
      element.style.opacity = String(opacity);
      element.style.transition = transition;
      element.style.borderRadius = String(borderRadius);
    },
    []
  );

  // Hover interactions
  const handleNavHover = useCallback(
    (e: MouseEvent) => {
      const element = navHoverEffect.current;
      if (!element) return;

      const target = e.target as HTMLElement;
      const current = e.currentTarget as HTMLElement;

      // Reset when hovering outside links
      if (target === current) {
        setNavHover(initialNavPosition);
        return;
      }

      // Otherwise, find the anchor element
      const navItemRef = getParentNodeByTag(target, "A");
      if (navItemRef) {
        setNavHover({
          width: navItemRef.offsetWidth,
          height: navItemRef.offsetHeight,
          left: navItemRef.offsetLeft,
          top: navItemRef.offsetTop,
          opacity: 1,
          borderRadius: getComputedStyle(navItemRef).borderRadius,
          transition:
            Number(element.style.opacity) > 0
              ? "0.18s all, 0.1s opacity"
              : "0s all, 0.1s 0.2s opacity",
        });
      }
    },
    [initialNavPosition, setNavHover]
  );

  // Keyboard focus interaction
  const handleNavFocus = useCallback(
    (e: FocusEvent) => {
      const element = navHoverEffect.current;
      if (!element) return;

      const currentTarget = e.currentTarget as HTMLElement;
      if (!currentTarget.matches(":focus-within")) {
        setNavHover(initialNavPosition);
        return;
      }

      const navItemRef = getParentNodeByTag(e.target as HTMLElement, "A");
      if (navItemRef) {
        setNavHover({
          width: navItemRef.offsetWidth,
          height: navItemRef.offsetHeight,
          left: navItemRef.offsetLeft,
          top: navItemRef.offsetTop,
          borderRadius: getComputedStyle(navItemRef).borderRadius,
          opacity: 1,
          transition:
            Number(element.style.opacity) > 0
              ? "0.18s all, 0.1s opacity"
              : "0s all, 0.1s 0.2s opacity",
        });
      }
    },
    [initialNavPosition, setNavHover]
  );

  const handleNavReset = useCallback(() => {
    setNavHover(initialNavPosition);
  }, [initialNavPosition, setNavHover]);

  // Attach events
  useEffect(() => {
    const element = navHoverEffect.current;
    if (!element) return;

    const parent = element.parentElement as HTMLElement;
    const links = parent.querySelectorAll("a");

    parent.addEventListener("mouseover", handleNavHover);
    parent.addEventListener("mouseleave", handleNavReset);
    parent.addEventListener("blur", handleNavFocus);

    links.forEach((link) => link.addEventListener("focus", handleNavFocus));

    return () => {
      parent.removeEventListener("mouseover", handleNavHover);
      parent.removeEventListener("mouseleave", handleNavReset);
      parent.removeEventListener("blur", handleNavFocus);

      links.forEach((link) => link.removeEventListener("focus", handleNavFocus));
    };
  }, [handleNavHover, handleNavFocus, handleNavReset]);

  return (
    <div
      ref={navHoverEffect}
      className={clsx(
        "button-border pointer-events-none absolute -z-10 select-none",
        "d:border-gray-300/50",
        className
      )}
      style={{ opacity: "0" }}
    />
  );
};
